import { differenceInCalendarDays, addMonths } from 'date-fns'
import { CONFIG, PRODUCT_LOAN_SIX_INSTALLMENTS_PAYDAY } from '../config'

// ----------------------------------
// Helper functions from CalculationHelper.php
// ----------------------------------

const ROUND_PRECISION = 2
const INSTALLMENT_DAYS_MIN = 20
const INSTALLMENT_DAYS_MAX = 50

/**
 * Adjusts a date to the last working day before it if it falls on a non-working day
 * @param {Date} date
 * @param {Array<string>} nonWorkingDates Array of date strings in Y-m-d format
 * @returns {Date}
 */
const adjustToLastWorkingDayBefore = (date, nonWorkingDates) => {
    if (!nonWorkingDates || nonWorkingDates.length === 0) {
        return date
    }

    let adjustedDate = new Date(date)
    const maxDaysBack = 7
    let daysChecked = 0

    const formatDate = (d) => {
        const year = d.getFullYear()
        const month = String(d.getMonth() + 1).padStart(2, '0')
        const day = String(d.getDate()).padStart(2, '0')
        return `${year}-${month}-${day}`
    }

    while (nonWorkingDates.includes(formatDate(adjustedDate))) {
        if (daysChecked >= maxDaysBack) {
            // Safety limit reached, return original date
            return date
        }

        adjustedDate.setDate(adjustedDate.getDate() - 1)
        daysChecked++
    }

    return adjustedDate
}

/**
 * Zarovná datum na cílový den v měsíci
 */
const alignToDay = (date, targetDay) => {
    const year = date.getFullYear()
    const month = date.getMonth()
    const lastDayOfMonth = new Date(year, month + 1, 0).getDate()
    const day = Math.min(targetDay ?? 1, lastDayOfMonth)
    return new Date(year, month, day, 23, 59, 59)
}

/**
 * Najde první validní datum splátky respektující minimální délku
 * @returns {[Date, Date]} [unadjusted date, adjusted date]
 */
const findFirstInstallmentDate = (startDate, targetDay, minDays, nonWorkingDates = null) => {
    let candidateUnadjusted = alignToDay(startDate, targetDay)
    let candidateAdjusted = candidateUnadjusted

    // Adjust for working day if non-working dates are provided
    if (nonWorkingDates !== null) {
        candidateAdjusted = adjustToLastWorkingDayBefore(candidateUnadjusted, nonWorkingDates)
    }

    while (differenceInCalendarDays(candidateAdjusted, startDate) < minDays) {
        candidateUnadjusted = addMonths(candidateUnadjusted, 1)
        candidateUnadjusted = alignToDay(candidateUnadjusted, targetDay)
        candidateAdjusted = candidateUnadjusted

        // Adjust for working day again after moving to next month
        if (nonWorkingDates !== null) {
            candidateAdjusted = adjustToLastWorkingDayBefore(candidateUnadjusted, nonWorkingDates)
        }
    }

    return [candidateUnadjusted, candidateAdjusted]
}

/**
 * Přidá měsíce a zarovná na cílový den
 */
const addMonthsAligned = (date, months, targetDay) => {
    const result = addMonths(date, months)
    return alignToDay(result, targetDay)
}

/**
 * Vypočítá datum splatnosti pro danou splátku
 */
const getDateExpected = (order, dateOfDayPaid, salaryPeriodicity, nonWorkingDates = null, startDate = null) => {
    const targetDay = salaryPeriodicity === 'lastDayOfMonth' ? 31 : dateOfDayPaid
    const start = startDate ?? new Date()

    // Find first valid instalment date (respecting minimum length and working days)
    // This returns both unadjusted and adjusted dates
    const [firstInstallmentDateUnadjusted, firstInstallmentDateAdjusted] = findFirstInstallmentDate(start, targetDay, INSTALLMENT_DAYS_MIN, nonWorkingDates)

    if (order <= 1) {
        return firstInstallmentDateAdjusted
    }

    // For subsequent instalments, add months to the UNADJUSTED date and align to target day
    // This ensures we don't compound the adjustments
    let subsequentDate = addMonthsAligned(firstInstallmentDateUnadjusted, order - 1, targetDay)

    // Adjust for working days if non-working dates are provided
    if (nonWorkingDates !== null) {
        subsequentDate = adjustToLastWorkingDayBefore(subsequentDate, nonWorkingDates)
    }

    return subsequentDate
}

// ----------------------------------
// SixInstallmentsPaydayCalculation.php
// ----------------------------------

class SixInstallmentsPaydayCalculation {
    constructor(amount, installments, options = {}) {
        this.amount = amount
        this.installmentCount = installments
        this.options = options

        this.principalCap = amount
        this.interestCap = amount
        this.table = {}
        this.startDate = new Date()

        this.prepareAmortizationTable()
    }

    // ------------- Helper methods -------------

    calculateInterestRate() {
        const FCA_DAILY_RATE_CAP = 0.008
        return Math.min(CONFIG[PRODUCT_LOAN_SIX_INSTALLMENTS_PAYDAY].interestRate, FCA_DAILY_RATE_CAP)
    }

    calculateInterestRateMonthly() {
        return CONFIG[PRODUCT_LOAN_SIX_INSTALLMENTS_PAYDAY].interestRateMonthly
    }

    calculateDateExpected(order) {
        return getDateExpected(order, this.options.dateOfDayPaid ?? 31, this.options.salaryPeriodicity ?? null, this.options.nonWorkingDates ?? null, this.startDate)
    }

    calculateDays(order) {
        if (order === 1) {
            return differenceInCalendarDays(this.calculateDateExpected(order), this.startDate)
        }

        return differenceInCalendarDays(this.calculateDateExpected(order), this.calculateDateExpected(order - 1))
    }

    calculateFirstInstallmentOffset() {
        return this.calculateDays(1) - 30
    }

    adjustInterestRate(interestRate) {
        const offset = this.calculateFirstInstallmentOffset()
        const days = 30 * this.installmentCount

        return (interestRate * (days + offset)) / days
    }

    // ------------- Core calculation methods -------------

    /**
     * InterestPerMonth - úrok za danou splátku (used for promo fee calculation)
     */
    iPmt(paymentNumber) {
        const interestRate = this.calculateInterestRate()
        let remaining = this.amount

        for (let order = 1; order < paymentNumber; order++) {
            // CAP
            const interest = Math.min(remaining * interestRate * this.table[order].days, this.table[order].pmt)

            const principal = this.table[order].pmt - interest
            remaining -= principal
        }

        // CAP
        return Math.min(remaining * interestRate * this.table[paymentNumber].days, this.table[paymentNumber].pmt)
    }

    /**
     * Binary search to find the equal payment amount that results in the correct principal payoff
     * while respecting the interest cap
     */
    findEqualPaymentAmount() {
        const interestRate = this.calculateInterestRate()

        // Initial estimate using standard annuity formula with adjusted rate
        const adjustedRate = this.adjustInterestRate(this.calculateInterestRateMonthly())
        let initialEstimate = (this.amount * adjustedRate) / (1 - Math.pow(1 + adjustedRate, -this.installmentCount))

        // Apply cap
        const maxPaymentCap = (this.principalCap + this.interestCap) / this.installmentCount
        initialEstimate = Math.min(initialEstimate, maxPaymentCap)

        // Binary search bounds
        let minPayment = this.principalCap / this.installmentCount
        let maxPayment = (this.principalCap + this.interestCap) / this.installmentCount

        const targetPrincipal = this.principalCap
        const tolerance = 0.01
        const maxIterations = 20

        let payment = initialEstimate

        for (let iteration = 0; iteration < maxIterations; iteration++) {
            let remaining = this.amount
            let totalPrincipalPaid = 0.0
            let totalInterestPaid = 0.0

            for (let order = 1; order <= this.installmentCount; order++) {
                let interest = remaining * interestRate * this.table[order].days

                // Apply interest cap
                const remainingInterestCap = this.interestCap - totalInterestPaid
                interest = Math.min(interest, remainingInterestCap)

                // Interest can't exceed payment
                interest = Math.min(interest, payment)

                const principal = payment - interest

                remaining -= principal
                totalPrincipalPaid += principal
                totalInterestPaid += interest
            }

            const difference = totalPrincipalPaid - targetPrincipal

            if (Math.abs(difference) < tolerance) {
                return payment
            }

            if (difference > 0) {
                maxPayment = payment
                payment = (minPayment + payment) / 2
            } else {
                minPayment = payment
                payment = (payment + maxPayment) / 2
            }
        }

        return payment
    }

    // ------------- Main amortization table logic -------------

    prepareAmortizationTable() {
        // First pass: calculate days for each instalment
        for (let order = 1; order <= this.installmentCount; order++) {
            const calendarDays = this.calculateDays(order)

            this.table[order] = {
                // Mirrors PHP SixInstallmentsPaydayCalculation.php:149.
                days: calendarDays + (order === 1 ? 1 : 0),
                calendarDays: calendarDays,
                dateExpected: this.calculateDateExpected(order),
                pmt: 0.0,
                iPmt: 0.0,
                pPmt: 0.0,
            }
        }

        // Find the equal payment amount via binary search
        const targetPayment = this.findEqualPaymentAmount()

        // Build the amortization table with the found payment
        const interestRate = this.calculateInterestRate()
        let remaining = this.amount
        let totalPrincipalAllocated = 0.0
        let totalInterestAllocated = 0.0

        for (let order = 1; order <= this.installmentCount; order++) {
            let interest = remaining * interestRate * this.table[order].days

            // Apply interest cap
            const remainingInterestCap = this.interestCap - totalInterestAllocated
            interest = Math.min(interest, remainingInterestCap)

            let principal

            if (order === this.installmentCount) {
                // Last instalment: use exact remaining amounts
                principal = this.principalCap - totalPrincipalAllocated
                interest = Math.min(interest, this.interestCap - totalInterestAllocated)
                this.table[order].pmt = principal + interest
            } else {
                this.table[order].pmt = targetPayment
                interest = Math.min(interest, targetPayment)
                principal = targetPayment - interest
            }

            this.table[order].iPmt = interest
            this.table[order].pPmt = principal

            remaining -= principal
            totalPrincipalAllocated += principal
            totalInterestAllocated += interest
        }
    }

    // ------------- Public API methods -------------

    /**
     * Mirrors CoreCalculation::reconcileLastInstallment() in
     * src/Monixa/ModelBundle/Entity/LoanInstallment/Calculation/CoreCalculation.php.
     * UKS-1599: the last instalment absorbs the rounding remainder, otherwise the rendered rows
     * sum a penny away from the total next to them and from the schedule stored by the backend.
     */
    reconcileLastInstallment(order, rawForOrder) {
        if (order !== this.installmentCount) {
            return this.round(rawForOrder(order))
        }

        let total = 0.0
        let allocated = 0.0

        for (let previous = 1; previous <= this.installmentCount; previous++) {
            total += rawForOrder(previous)

            if (previous < order) {
                allocated += this.round(rawForOrder(previous))
            }
        }

        return this.round(this.round(total) - allocated)
    }

    calculateAmount(order) {
        return this.reconcileLastInstallment(order, (current) => this.table[current].pPmt)
    }

    calculateFee(order) {
        return this.reconcileLastInstallment(order, (current) => this.table[current].iPmt)
    }

    calculateAmountFee(order) {
        const amountFee = this.calculateAmount(order) + this.calculateFee(order)
        return this.round(amountFee)
    }

    round(value) {
        return Math.round(value * Math.pow(10, ROUND_PRECISION)) / Math.pow(10, ROUND_PRECISION)
    }

    // ------------- Export table for debugging -------------

    getTable() {
        return this.table
    }
}

export const generateRawPayments = (table, installments) => {
    const payments = []

    let days = 0
    for (let order = 1; order <= installments; order++) {
        days += table[order].calendarDays

        payments.push({
            amount: table[order].pPmt + table[order].iPmt,
            fee: 0.0,
            day: days,
        })
    }

    return payments
}

// ----------------------------------
// Export
// ----------------------------------

export default SixInstallmentsPaydayCalculation
